import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate } from 'react-router-dom';
import { useEffect, type ReactNode } from 'react';
import { useGymStore } from '@/stores/gymStore';
import { AuthProvider } from '@/contexts/AuthContext';
import { BreadcrumbProvider } from '@/contexts/BreadcrumbContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { OnboardingProvider } from '@/components/onboarding';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { AppLayout } from '@/components/layout/AppLayout';
import { LoginPage } from '@/pages/LoginPage';
import { ProfilePage } from '@/pages/ProfilePage';
import { GymsPage } from '@/pages/GymsPage';
import { ClimbPage } from '@/pages/ClimbPage';
import { GymSettingsPage } from '@/pages/GymSettingsPage';
import { AnalyticsPage } from '@/pages/AnalyticsPage';
import { CreateRoutePage } from '@/pages/CreateRoutePage';
import { EditRoutePage } from '@/pages/EditRoutePage';
import { AcceptInvitationPage } from '@/pages/AcceptInvitationPage';
import { AcceptOwnerInvitationPage } from '@/pages/AcceptOwnerInvitationPage';
import { RegisterGymPage } from '@/pages/RegisterGymPage';
import { MyGymRequestsPage } from '@/pages/MyGymRequestsPage';
import { GymRequestDetailPage } from '@/pages/GymRequestDetailPage';
import { MapEditorPage } from '@/pages/MapEditorPage';
import { AdminDashboardPage } from '@/pages/admin/AdminDashboardPage';
import { AdminGymRequestsPage } from '@/pages/admin/AdminGymRequestsPage';
import { AdminGymRequestReviewPage } from '@/pages/admin/AdminGymRequestReviewPage';
import { AdminUsersPage } from '@/pages/admin/AdminUsersPage';
import { AdminCreateGymPage } from '@/pages/admin/AdminCreateGymPage';
import { AdminAssignRolesPage } from '@/pages/admin/AdminAssignRolesPage';
import { AdminFeedbackPage } from '@/pages/admin/AdminFeedbackPage';
import { AdminFlyerGeneratorPage } from '@/pages/admin/AdminFlyerGeneratorPage';
import { AdminRoute } from '@/components/admin';
import { HomeRedirect } from '@/components/routing';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { IconPreviewPage } from '@/pages/IconPreviewPage';
import { InstallPrompt } from '@/components/pwa/InstallPrompt';
import { IOSInstallPrompt } from '@/components/pwa/IOSInstallPrompt';
import { SkeletonStyles } from '@/components/ui';

/**
 * Main content wrapper for consistent layout
 */
function MainContent({ children }: { children: ReactNode }) {
  return <div style={{ flex: 1, overflow: 'auto' }}>{children}</div>;
}

/**
 * Redirect component that sets the active gym and redirects to /climb
 * Used to handle old /gyms/:gymId URLs
 */
const GymRedirect = () => {
  const { gymId } = useParams<{ gymId: string }>();
  const navigate = useNavigate();
  const setActiveGymId = useGymStore((state) => state.setActiveGymId);

  useEffect(() => {
    if (gymId) {
      setActiveGymId(gymId);
    }
    navigate('/climb', { replace: true });
  }, [gymId, setActiveGymId, navigate]);

  return null;
};

/**
 * Main application component
 */
export const App = () => {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <BreadcrumbProvider>
            <OnboardingProvider>
              {/* Main Content - flex container for proper scrolling */}
              <MainContent>
                <Routes>
                  {/* Public routes */}
                  <Route path="/login" element={<LoginPage />} />

                  {/* Dev/Preview routes */}
                  <Route path="/icon-preview" element={<IconPreviewPage />} />

                  {/* Invitation acceptance (public, handles own auth) */}
                  <Route path="/invitations/:token" element={<AcceptInvitationPage />} />

                  {/* Owner invitation acceptance (public, handles own auth) */}
                  <Route path="/accept-owner-invitation" element={<AcceptOwnerInvitationPage />} />

                  {/* Smart redirect: first-time users → /gyms, returning users → last visited or /climb */}
                  <Route path="/" element={<HomeRedirect />} />

                  {/* Protected routes */}
                  <Route
                    path="/profile"
                    element={
                      <ProtectedRoute>
                        <AppLayout>
                          <ProfilePage />
                        </AppLayout>
                      </ProtectedRoute>
                    }
                  />

                  {/* Redirect /settings to profile settings tab */}
                  <Route
                    path="/settings"
                    element={
                      <ProtectedRoute>
                        <Navigate to="/profile?tab=settings" replace />
                      </ProtectedRoute>
                    }
                  />

                  {/* Tab routes with bottom navigation */}
                  <Route
                    path="/gyms"
                    element={
                      <ProtectedRoute>
                        <AppLayout>
                          <GymsPage />
                        </AppLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/climb"
                    element={
                      <ProtectedRoute>
                        <AppLayout>
                          <ClimbPage />
                        </AppLayout>
                      </ProtectedRoute>
                    }
                  />

                  {/* Redirect old gym detail URL to new climb page */}
                  <Route
                    path="/gyms/:gymId"
                    element={
                      <ProtectedRoute>
                        <GymRedirect />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/gyms/:gymId/settings"
                    element={
                      <ProtectedRoute>
                        <GymSettingsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/gyms/:gymId/settings/floor-plan/editor"
                    element={
                      <ProtectedRoute>
                        <MapEditorPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/gyms/:gymId/analytics"
                    element={
                      <ProtectedRoute>
                        <AnalyticsPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Redirect old routes page to gym detail */}
                  <Route path="/gyms/:gymId/routes" element={<Navigate to=".." replace />} />
                  {/* Redirect old route detail page to climb page */}
                  <Route path="/routes/:routeId" element={<Navigate to="/climb" replace />} />

                  {/* Route management routes */}
                  <Route
                    path="/gyms/:gymId/routes/new"
                    element={
                      <ProtectedRoute>
                        <CreateRoutePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/routes/:routeId/edit"
                    element={
                      <ProtectedRoute>
                        <EditRoutePage />
                      </ProtectedRoute>
                    }
                  />

                  {/* My gym requests - accessible to all authenticated users */}
                  <Route
                    path="/gym-requests"
                    element={
                      <ProtectedRoute>
                        <MyGymRequestsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/gym-requests/:id"
                    element={
                      <ProtectedRoute>
                        <GymRequestDetailPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Admin routes (requires admin role) */}
                  <Route element={<AdminRoute />}>
                    <Route path="/admin" element={<AdminDashboardPage />} />
                    <Route path="/admin/gym-requests" element={<AdminGymRequestsPage />} />
                    <Route path="/admin/gym-requests/:id" element={<AdminGymRequestReviewPage />} />
                    <Route path="/admin/users" element={<AdminUsersPage />} />
                    <Route path="/admin/create-gym" element={<AdminCreateGymPage />} />
                    <Route path="/admin/assign-roles" element={<AdminAssignRolesPage />} />
                    <Route path="/admin/feedback" element={<AdminFeedbackPage />} />
                    <Route path="/admin/flyer-generator" element={<AdminFlyerGeneratorPage />} />
                    {/* Gym registration - admin only for now */}
                    <Route path="/gyms/register" element={<RegisterGymPage />} />
                  </Route>

                  {/* 404 catch-all route */}
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </MainContent>

              {/* PWA Install Prompts */}
              <InstallPrompt />
              <IOSInstallPrompt />

              {/* Global skeleton animation styles */}
              <SkeletonStyles />
            </OnboardingProvider>
          </BreadcrumbProvider>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
};
